import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Privacy Policy | Aspirant";
  }, []);

  return (
    <div className="bg-[#06010a] text-white font-poppins overflow-x-hidden">
      {/* Hero Section */}
      <section className="flex flex-col justify-center items-center text-center min-h-[60vh] px-4 bg-gradient-to-b from-[#06010a] to-[#260345]">
        <h1 className="text-4xl md:text-6xl font-bold mb-6">
          Privacy{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-purple-600 font-extrabold">
            Policy
          </span>
        </h1>

        <p className="text-lg text-gray-300 max-w-2xl">
          Novistep (“Aspirant”, “we”, “our”, or “us”) respects your privacy and 
          is committed to protecting your personal data through transparent processing.
        </p>

        <button
          onClick={() => navigate("/")}
          className="mt-8 bg-[#ffc403] text-black font-bold px-6 py-3 rounded-xl hover:bg-[#e6b800] transition"
        >
          Back to Home
        </button>
      </section>

      {/* Content Section */}
      <section className="bg-white text-black py-20 px-6 md:px-8">
        <div className="max-w-4xl mx-auto space-y-10">
          
          {/* Intro */}
          <div>
            <h2 className="text-2xl font-bold mb-3">1. Scope of Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This Privacy Policy explains how we collect, use, store, and protect your information when you use our website and application available at <strong>https://job.novistep.com</strong>.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By accessing or using the Platform, you agree to the terms of this Privacy Policy and our data handling practices.
            </p>
          </div>

          {/* Information Collection */}
          <div>
            <h2 className="text-2xl font-bold mb-3">2. Information We Collect</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We collect information to provide and improve our AI-powered career and study-abroad services:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Personal Data:</strong> Full name, email address, phone number, educational background, and employment history.</li>
              <li><strong>Documents:</strong> Resumes, profiles, and other documents you upload for AI optimization.</li>
              <li><strong>Usage Data:</strong> IP address, device type, browser type, and interaction data within the Platform.</li>
              <li><strong>Payments:</strong> We do not store card details; all payments are processed securely via Razorpay.</li>
            </ul>
          </div>

          {/* Use of Information */}
          <div>
            <h2 className="text-2xl font-bold mb-3">3. How We Use Your Data</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your information is utilized to enhance your experience and deliver our core services:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Managing user accounts and providing AI-generated career insights.</li>
              <li>Processing subscriptions and transactions securely.</li>
              <li>Improving platform performance and refining our AI models.</li>
              <li>Providing customer support and sending service-related notifications.</li>
              <li>Ensuring platform security and preventing fraudulent activities.</li>
            </ul>
          </div>

          {/* AI Processing */}
          <div>
            <h2 className="text-2xl font-bold mb-3">4. AI & Data Processing</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our platform uses advanced AI systems to process your data:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>User-provided data is processed by AI to generate resumes and interview simulations.</li>
              <li>We do not use your personal data to train public or third-party AI models without your explicit consent.</li>
              <li>AI outputs are generated specifically based on your inputs and usage patterns.</li>
            </ul>
          </div>

          {/* Data Sharing */}
          <div>
            <h2 className="text-2xl font-bold mb-3">5. Data Sharing & Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell or rent personal data. We share information only with trusted partners:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Service Providers:</strong> Payment gateways (Razorpay), cloud hosting providers, and analytics tools.</li>
              <li><strong>Legal Requirements:</strong> Law enforcement or government bodies when required by applicable laws.</li>
            </ul>
          </div>

          {/* Security */}
          <div>
            <h2 className="text-2xl font-bold mb-3">6. Data Security & Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              We implement encrypted connections and restricted access protocols to protect your data. We retain your personal data for as long as your account is active or as required to provide services and comply with legal obligations in India.
            </p>
          </div>

          {/* User Rights */}
          <div>
            <h2 className="text-2xl font-bold mb-3">7. Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              As a user, you have the following rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>The right to access and correct your personal data.</li>
              <li>The right to request data deletion.</li>
              <li>The right to withdraw consent for data processing at any time.</li>
            </ul>
          </div>

          {/* Compliance */}
          <div>
            <h2 className="text-2xl font-bold mb-3">8. Compliance & Legal Framework</h2>
            <p className="text-gray-700 leading-relaxed">
              This policy complies with the Information Technology Act, 2000, and the Digital Personal Data Protection Act (DPDP Act) of India. Any disputes are subject to the exclusive jurisdiction of the courts located in India.
            </p>
          </div>

          {/* Contact Box (Matches Sample exactly) */}
          <div className="bg-gray-50 border rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-2">Contact Legal Support</h2>
            <p className="text-gray-700 text-sm mb-4">
              If you have any questions regarding this Privacy Policy or wish to exercise your data rights, please reach out to our team.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="mailto:info@novistep.com"
                className="inline-block bg-yellow-400 text-black font-semibold px-6 py-2 rounded-lg hover:bg-yellow-500 transition"
              >
                Email Support
              </a>
              <a
                href="https://job.novistep.com"
                target="_blank"
                rel="noreferrer"
                className="inline-block bg-gray-200 text-black font-semibold px-6 py-2 rounded-lg hover:bg-gray-300 transition"
              >
                Visit Website
              </a>
            </div>
          </div>
          
          <p className="text-center text-gray-400 text-sm pt-10">
            Last Updated: January 30, 2025 | © {new Date().getFullYear()} Novistep / Aspirant.
          </p>
        </div>  
      </section>
    </div>
  );
};

export default PrivacyPolicy;